import React, { memo, useCallback, useEffect, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useToast } from '@chakra-ui/core';
import { TFunction, useTranslation } from 'react-i18next';
import { LogoSlotConfig } from './CompanyLogoType';
import LogoUploader from './CompanyLogoUploader';

const readFileAsDataUrl = (file: File, t: TFunction<'uploadCompanyLogo'>): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error(t('errors.readingFileError')));
    reader.readAsDataURL(file);
  });

const extractErrorMessage = (err: unknown, fallback: string): string =>
  axios.isAxiosError(err)
    ? (err as AxiosError<{ message?: string }>).response?.data?.message ?? /* istanbul ignore next */ fallback
    : fallback;

interface CompanyLogoSlotProps {
  accountId?: string;
  config: LogoSlotConfig;
  saveCompanyLogo: (payload: { accountId: string; companyLogo: string; isSquare: boolean }) => Promise<unknown>;
  getCompanyLogo: (payload: { accountId: string; isSquare: boolean }) => Promise<string>;
}

const CompanyLogoSlot: React.FC<CompanyLogoSlotProps> = memo(({ accountId, config, saveCompanyLogo, getCompanyLogo }) => {
  const { t } = useTranslation('uploadCompanyLogo');
  const toast = useToast();
  const [logoUrl, setLogoUrl] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!accountId) return;

    let isMounted = true;
    setIsLoading(true);
    setError(undefined);

    getCompanyLogo({ accountId, isSquare: config.isSquare })
      .then((result) => {
        if (!isMounted) return;
        setLogoUrl(result || undefined);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        /* istanbul ignore next */
        if (!isMounted) return;
        setError(extractErrorMessage(err, t('errors.logoLoadError')));
        setIsLoading(false);
      });

    // eslint-disable-next-line consistent-return
    return () => {
      isMounted = false;
    };
  }, [accountId, config.isSquare, getCompanyLogo, t]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!accountId) return;
      setIsSaving(true);
      setError(undefined);
      try {
        const dataUrl = await readFileAsDataUrl(file, t);
        await saveCompanyLogo({ accountId, companyLogo: dataUrl, isSquare: config.isSquare });
        setLogoUrl(dataUrl);
        toast({ title: t('uploadButton.successMessage'), status: 'success', duration: 3000, isClosable: true });
      } catch (err: unknown) {
        setError(extractErrorMessage(err, t('errors.uploadFailed')));
      } finally {
        setIsSaving(false);
      }
    },
    [accountId, config.isSquare, saveCompanyLogo, t, toast],
  );

  const handleDelete = useCallback(async () => {
    if (!accountId) return;
    setIsSaving(true);
    setError(undefined);
    try {
      await saveCompanyLogo({ accountId, companyLogo: '', isSquare: config.isSquare });
      setLogoUrl(undefined);
      toast({ title: t('deleteButton.successMessage'), status: 'success', duration: 3000, isClosable: true });
    } catch (err: unknown) {
      setError(extractErrorMessage(err, t('errors.deleteFailed')));
    } finally {
      setIsSaving(false);
    }
  }, [accountId, config.isSquare, saveCompanyLogo, t, toast]);

  const handleDismissError = useCallback(() => setError(undefined), []);

  return (
    <LogoUploader
      config={config}
      currentLogoUrl={logoUrl}
      isLoading={isLoading}
      isSaving={isSaving}
      error={error}
      onUpload={handleUpload}
      onDelete={handleDelete}
      onDismissError={handleDismissError}
    />
  );
});

CompanyLogoSlot.displayName = 'CompanyLogoSlot';

export default CompanyLogoSlot;
